import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Flex,
  Avatar,
  Badge,
  IconButton,
  Text,
  TextProps,
  Link,
  Switch,
  Tooltip,
} from "@radix-ui/themes";
import { BrainIcon } from "lucide-react";
import { PiTranslate } from "react-icons/pi";

import { radixColors } from "@/components/radixColors";
import { CommentValue } from "@/api/ticket-transcript/TicketTranscript.model";
import { CommentSources } from "@/v2/components/messageSources";
import { ChainOfThoughtsButton } from "@/v2/components/chainOfThoughtsButton";
import { CommentBodyWithBoundary as ExperimentalBody } from "./CommentBody";
import { MessageSources } from "@/routes/simulator/messages/messageSources";
import { DATE_TIME_FORMAT } from "@/v2/assets/utils";
import { TrainMessage, TrainMessageUpTo } from "@/v2/components/trainMessage";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import {
  AiResponseThought,
  AiAgentStep,
} from "@/v2/entities/chain-of-thoughts/ChainOfThoughts.model";
import { useSuggestedReplyAuiByCommentId } from "@/v2/entities/suggested-reply-aui/SuggestedReplyAui.api";
import { Tooltip as SharedTooltip } from "@/components/shared/tooltip";

import { ImageViewer } from "./ImageViewer";

function isHtmlString(text: string): boolean {
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(text);
}

function extractUrls(text: string): string | undefined {
  const urlPattern = /https?:\/\/[^\s]+/;
  const match = text.match(urlPattern);

  if (match) {
    return match[0].replace(/[^\w\/]+$/, ""); // Remove trailing non-word characters
  }
}

function getCommentBodyStyles(color: string): TextProps {
  return {
    size: "2",
    color: "gray",
    highContrast: true,
    style: {
      background: `var(--${color}-a3)`,
      padding: "8px",
      borderRadius: "8px",
      wordBreak: "break-word",
      whiteSpace: "break-spaces",
    },
  };
}

interface CommentBodyProps {
  body: string;
  color: (typeof radixColors)[number] | "amber";
}

function CommentBody({ body, color }: CommentBodyProps) {
  if (isHtmlString(body)) {
    return (
      <Text
        {...getCommentBodyStyles(color)}
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }

  return (
    <Text {...getCommentBodyStyles(color)}>
      {body
        .trim()
        .split(/(https?:\/\/[^\s]+)/g)
        .map((part, index) => {
          const cleanedUrls = extractUrls(part) ?? "";

          if (part.match(/https?:\/\/[^\s]+/)) {
            if (cleanedUrls.match(/\.(jpeg|jpg|gif|png)$/)) {
              return <ImageViewer key={index} url={cleanedUrls} />;
            }

            return (
              <Link
                key={index}
                color="indigo"
                underline={"hover"}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
              >
                {part}
              </Link>
            );
          }

          return (
            <span key={index} dangerouslySetInnerHTML={{ __html: part }} />
          );
        })}
    </Text>
  );
}

interface CommentProps extends CommentValue {
  previousCommentId?: string;
  authorName: string;
  color: (typeof radixColors)[number];
  isFirst?: boolean;
  hideHeader?: boolean;
  hasConscutiveComment?: boolean;
  suggestedReplies: {
    contextExport: any[];
    aiResponseThoughts?: AiResponseThought[];
    steps?: AiAgentStep[];
  }[];
  chooseComment?: (commentId: string, comment: string) => void;
  chooseMessagesUpTo?: (targetCommentId: string) => void;
  onChainOfThoughtsClick?: (comment: CommentValue) => void;
  translateAll?: boolean;
  isTranslated?: boolean;
  onTranslate?: (
    messageBody: string,
    shouldTranslate: boolean
  ) => Promise<void>;
  isTranslating?: boolean;
  originalBody?: string;
}

export function Comment({
  previousCommentId,
  authorName,
  createdAt,
  body,
  isInternal,
  isFirst = false,
  hideHeader = false,
  hasConscutiveComment = false,
  suggestedReplies,
  chooseComment,
  chooseMessagesUpTo,
  onChainOfThoughtsClick,
  agent,
  translateAll = false,
  isTranslated = false,
  onTranslate,
  isTranslating = false,
  originalBody,
  ...props
}: CommentProps) {
  const color = isInternal ? "amber" : props.color;
  const [open, setOpen] = useState(false);
  const [showAuiMock, setShowAuiMock] = useState(false);
  const suggestedReplyAui = useFlag("suggestedReplyAui", false);

  const showChainOfThoughts = useMemo(
    () =>
      (!!onChainOfThoughtsClick || !chooseComment) &&
      Array.isArray(suggestedReplies?.[0]?.aiResponseThoughts?.[0]?.steps) &&
      suggestedReplies?.[0]?.aiResponseThoughts?.[0]?.steps?.length > 0,
    [chooseComment, onChainOfThoughtsClick, suggestedReplies]
  );

  const { data: suggestedReplyAuiData } = useSuggestedReplyAuiByCommentId(
    previousCommentId?.toString() ?? "",
    !!(suggestedReplyAui && showChainOfThoughts)
  );

  const hasAuiResponse = !!(suggestedReplyAuiData?.body);
  const auiBody = suggestedReplyAuiData?.body;

  const handleAuiToggle = useCallback((checked: boolean) => {
    setShowAuiMock(checked);
  }, []);

  const agentInitials = useMemo(() => {
    const words = authorName
      .replaceAll("Unknown ", "")
      .split(" ")
      .map((x) => x[0]);

    if (words.length === 1) {
      return words[0];
    }

    return [words[0], words[words.length - 1]].join("");
  }, [authorName]);

  return (
    <Flex
      pt={isFirst ? "3" : undefined}
      pb={hasConscutiveComment ? undefined : "6"}
      direction={"column"}
      gap={"1"}
    >
      {!hideHeader && (
        <Flex
          p={"2"}
          justify={"between"}
          gap={"2"}
          align={"center"}
          id={props.externalId.toString()}
        >
          <Flex gap={"2"} align={"center"}>
            <Avatar
              size={"1"}
              fallback={agentInitials}
              color={color}
              radius={"full"}
            />

            <Text
              size={"2"}
              color={"gray"}
              weight={"medium"}
              highContrast
              truncate
            >
              {authorName ?? "Unknown Author"}
            </Text>

            <Flex gap={"2"} align={"center"} style={{ flexShrink: 0 }}>
              <Text size={"1"} color={"gray"}>
                {dayjs(createdAt).format(DATE_TIME_FORMAT)}
              </Text>
              {showChainOfThoughts && onChainOfThoughtsClick && chooseComment && (
                <Tooltip content="View chain of thought">
                  <IconButton
                    size="1"
                    variant="outline"
                    color="gray"
                    onClick={() => {
                      onChainOfThoughtsClick({
                        externalId: props.externalId,
                        createdAt,
                        updatedAt: props.updatedAt,
                        authorId: props.authorId,
                        authorName,
                        authorEmail: props.authorEmail,
                        agent,
                        isInternal,
                        body,
                        rawBody: props.rawBody,
                        suggestedReplies,
                      });
                    }}
                  >
                    <BrainIcon size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Flex>
          </Flex>

          {isInternal && (
            <Badge size={"2"} variant={"surface"} color={color} highContrast>
              Internal
            </Badge>
          )}
        </Flex>
      )}

      <Flex pt={hideHeader ? "1" : undefined} pl={"6"} pr={"4"}>
        <ExperimentalBody
          color={color}
          body={
            suggestedReplyAui && showAuiMock && auiBody
              ? auiBody
              : body
          }
        />
      </Flex>
      <Flex justify={"end"} pr={"4"} pt={"1"} gap={"2"}>
        {onTranslate && !translateAll && (
          <Tooltip
            content={isTranslated ? "Show original" : "Translate to English"}
          >
            <Flex align={"center"} gap={"1"}>
              <PiTranslate style={{ color: "var(--gray-11)" }} />
              <Switch
                size="1"
                disabled={isTranslating}
                checked={isTranslated}
                onCheckedChange={async (checked) => {
                  await onTranslate(originalBody || body, checked);
                }}
              />
            </Flex>
          </Tooltip>
        )}
        {showChainOfThoughts && !chooseComment && (
          <Flex gap="2" align="center">
            <ChainOfThoughtsButton
              source={{
                id: props.externalId.toString(),
                chatMessageId: props.externalId.toString(),
                toolResults: null,
                contextExport: suggestedReplies?.[0]?.contextExport,
                toolCalls: null,
                chainOfThoughtSteps:
                  suggestedReplies?.[0]?.aiResponseThoughts?.[0]?.steps || [],
              }}
              onClick={() => {
                onChainOfThoughtsClick?.({
                  externalId: props.externalId,
                  createdAt,
                  updatedAt: props.updatedAt,
                  authorId: props.authorId,
                  authorName,
                  authorEmail: props.authorEmail,
                  agent,
                  isInternal,
                  body,
                  rawBody: props.rawBody,
                  suggestedReplies,
                });
              }}
            />
            {suggestedReplyAui && (
              <Flex align="center" gap="1">
                <Text size="1" color="gray">
                  AUI
                </Text>
                {hasAuiResponse ? (
                  <Switch
                    size="1"
                    checked={showAuiMock}
                    onCheckedChange={handleAuiToggle}
                  />
                ) : (
                  <SharedTooltip>
                    <SharedTooltip.Trigger>
                      <span style={{ display: "inline-flex" }}>
                        <Switch
                          size="1"
                          checked={false}
                          onCheckedChange={() => {}}
                          disabled
                        />
                      </span>
                    </SharedTooltip.Trigger>
                    <SharedTooltip.Content>
                      No response generated
                    </SharedTooltip.Content>
                  </SharedTooltip>
                )}
              </Flex>
            )}
          </Flex>
        )}
        {chooseMessagesUpTo && !isFirst && !agent && (
          <TrainMessageUpTo
            onTrain={() => chooseMessagesUpTo?.(props.externalId.toString())}
          />
        )}
        {chooseComment && (
          <TrainMessage
            onTrain={() => chooseComment?.(props.externalId.toString(), body)}
          />
        )}
      </Flex>

      <MessageSources
        isOpen={open}
        setOpen={setOpen}
        sources={suggestedReplies?.[0]?.contextExport ?? null}
        disableForgetIt
      />
    </Flex>
  );
}
